import { Button, Spinner } from '@heroui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useGetSurveyQuery } from '@/patient-app-common/api/surveyApi';
import useResults from '@/patient-app-common/hooks/useResults';
import { useGetLastTrackerQuery } from '@/patient-app-common/api/trackerApi';
import { ScrollFooter } from '@/components/layout/ScrollFooter';

import { ResultCard } from './ResultCard';

export function Result() {
  const { t } = useTranslation('survey');
  const navigate = useNavigate();
  const { state } = useLocation();
  const { surveyId } = useParams();
  const patientEntry = state?.patientEntry;

  const { data: fetchedEntry, isFetching: isFetchingEntry } =
    useGetLastTrackerQuery(surveyId, {
      skip: patientEntry || !surveyId,
    });

  const { data: survey, isFetching: isFetchingSurvey } = useGetSurveyQuery(
    surveyId,
    {
      skip: !surveyId,
    }
  );

  const { createResults } = useResults();

  const isFetching = isFetchingEntry || isFetchingSurvey;
  const [loader, setLoader] = useState(true);
  const [recommendations, setRecommendations] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoader(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (!survey) return;

    const entry = patientEntry || fetchedEntry;
    if (!entry) return;

    if (entry.createdAt < survey.lastEntryDate) return;

    const parseRecommendations = async () => {
      setLoader(true);
      const results = await createResults(survey, entry);
      setRecommendations(results);
      setLoader(false);
    };

    parseRecommendations();
  }, [survey, patientEntry, fetchedEntry]);

  const hasCharts = useMemo(() => {
    if (!survey || survey.metricTracker) return false;
    return survey.questions.find((question) => question.chart);
  }, [survey]);

  const handleExit = () => {
    if (state?.hasNextSurvey) {
      navigate(state?.nextSurveyPathname, {
        state: { eventId: state?.nextSurveyEventId },
      });
    } else {
      const pathname = hasCharts ? `/recommendation/charts/${survey.id}` : '/';
      navigate({ pathname });
    }
  };

  if (!loader && !recommendations) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col min-h-[100dvh] relative">
      <main className="flex flex-1 justify-center overflow-y-auto">
        <div className="flex flex-col w-full max-w-[800px] text-left p-4 space-y-4">
          {isFetching && <Spinner />}
          {recommendations?.length &&
            recommendations.map((recommendation) => (
              <ResultCard
                key={recommendation.id}
                recommendation={recommendation}
              />
            ))}
        </div>
      </main>
      <footer className="bg-background sticky bottom-0 z-[10]">
        <ScrollFooter dependencies={[recommendations]} />
        <div className="flex justify-center p-4 space-x-4">
          <Button
            className="w-1/2 md:w-1/4"
            color="primary"
            onPress={handleExit}
            size="lg"
          >
            {t('common.action.ok')}
          </Button>
        </div>
      </footer>
    </div>
  );
}
